import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';
import { formatoDiferencia, formatoPorcentaje, formatoPrecio } from '@/lib/utils/formato-precio';

/**
 * Un precio (mapa de diseño 226, §3): dos decimales, coma decimal, números
 * tabulares en la letra normal, nunca monoespaciada. «Bs» sólo en un monto
 * suelto (`conBs`); en una tabla va en el encabezado de la columna. Sin valor,
 * «—»: ausente es «no sé», nunca 0.
 */
export function Precio({
  valor,
  conBs = false,
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { valor: number | null | undefined; conBs?: boolean }) {
  if (valor == null)
    return (
      <span className={cn('tabular-nums text-muted-foreground', className)} {...props}>
        —
      </span>
    );
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)} {...props}>
      {conBs ? `Bs ${formatoPrecio(valor)}` : formatoPrecio(valor)}
    </span>
  );
}

export type SentidoDelCambio = 'SUBE' | 'BAJA' | 'IGUAL';

/** Hacia dónde va una diferencia de precio, al centavo. */
export const sentidoDe = (diferencia: number): SentidoDelCambio =>
  Math.round(diferencia * 100) > 0 ? 'SUBE' : Math.round(diferencia * 100) < 0 ? 'BAJA' : 'IGUAL';

export const TINTA_DEL_CAMBIO: Record<SentidoDelCambio, string> = {
  SUBE: 'text-ok',
  BAJA: 'text-baja',
  IGUAL: 'text-muted-foreground',
};

/**
 * El cambio de un precio: «−47,48 −20,7 %» en `baja` si baja, «+1.366,40 +22,7 %»
 * en `ok` si sube. El color dice hacia dónde va, no si está bien (mapa 226).
 * Sin porcentaje (un precio de antes en 0), sólo la diferencia.
 */
export function CambioDePrecio({
  diferencia,
  porcentaje,
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { diferencia: number; porcentaje?: number | null }) {
  const sentido = sentidoDe(diferencia);
  return (
    <span
      data-testid="cambio-de-precio"
      data-sentido={sentido}
      className={cn(
        'whitespace-nowrap font-medium tabular-nums',
        TINTA_DEL_CAMBIO[sentido],
        className
      )}
      {...props}
    >
      {formatoDiferencia(diferencia)}
      {porcentaje != null && Number.isFinite(porcentaje) && (
        <small className="ml-1 text-xs font-normal opacity-85">
          {formatoPorcentaje(porcentaje, { signo: true })}
        </small>
      )}
    </span>
  );
}
